// PCM conversions at the audio edges: the microphone's floats to the
// s16le mono the contract's Up.audio carries, and the reply's s16le
// bytes back to floats for the player. Pure — everything here is
// testable without a device.
import type { AudioSink, MicSource } from "@/audio/ports";

// floatToPcm16 clamps to the range and rounds: the microphone's
// Float32 samples (−1…1) to int16 little-endian bytes. The scale is
// 0x8000 both ways and +1 clamps, so halves stay exact.
export function floatToPcm16(samples: Float32Array): Uint8Array {
  const out = new Uint8Array(samples.length * 2);
  const view = new DataView(out.buffer);
  for (let i = 0; i < samples.length; i++) {
    const v = Math.round(Math.max(-1, Math.min(1, samples[i])) * 0x8000);
    view.setInt16(i * 2, Math.max(-0x8000, Math.min(0x7fff, v)), true);
  }
  return out;
}

// pcm16ToFloat decodes s16le mono to floats. A chunk can end in the
// middle of a sample: the leftover byte comes back, and the caller
// prepends it to the next chunk — dropping it shifted every sample
// after it by one and the rest of a reply came out as noise.
export function pcm16ToFloat(bytes: Uint8Array, odd: Uint8Array): { floats: Float32Array<ArrayBuffer>; odd: Uint8Array } {
  const whole = new Uint8Array(odd.length + bytes.length);
  whole.set(odd, 0);
  whole.set(bytes, odd.length);
  const pairs = whole.length >> 1;
  const floats = new Float32Array(pairs);
  const view = new DataView(whole.buffer, whole.byteOffset, whole.byteLength);
  for (let i = 0; i < pairs; i++) {
    const v = view.getInt16(i * 2, true);
    floats[i] = v / 0x8000;
  }
  return { floats, odd: whole.length & 1 ? whole.slice(pairs * 2) : new Uint8Array(0) };
}

// batches the worklet's small blocks into ~periodMs of audio a frame,
// so the socket does not carry a message per 128 samples; flush
// releases a partial remainder (a turn's last words), drop throws one
// away (a misfire's, an interrupted turn's — it must not open the next
// turn).
export function batcher(rate: number, periodMs: number): {
  push(block: Float32Array): Uint8Array | null;
  flush(): Uint8Array | null;
  drop(): void;
} {
  let buf = new Float32Array(0);
  const want = Math.max(1, Math.round((rate * periodMs) / 1000));
  const pack = (): Uint8Array => {
    const out = floatToPcm16(buf);
    buf = new Float32Array(0);
    return out;
  };
  return {
    push(block) {
      const joined = new Float32Array(buf.length + block.length);
      joined.set(buf, 0);
      joined.set(block, buf.length);
      buf = joined;
      return buf.length >= want ? pack() : null;
    },
    flush() {
      return buf.length > 0 ? pack() : null;
    },
    drop() {
      buf = new Float32Array(0);
    },
  };
}

// The ports the audio edges speak, named once: the microphone side and
// the sink the player schedules on. The runtime provides them; the
// tests bring their own.
export type { AudioSink, MicSource };
